import {
  type ApplicationConfig,
  inject,
  LOCALE_ID,
  provideAppInitializer,
  provideBrowserGlobalErrorListeners,
  provideZoneChangeDetection,
} from '@angular/core';
import { MAT_DATE_LOCALE } from '@angular/material/core';
import { provideDateFnsAdapter } from '@angular/material-date-fns-adapter';
import { provideRouter, withInMemoryScrolling } from '@angular/router';
import { provideApi } from '@carplus/openapi-client';
import { provideTransloco, TranslocoService } from '@jsverse/transloco';
import { provideTranslocoMessageformat } from '@jsverse/transloco-messageformat';
import { firstValueFrom } from 'rxjs';
import { it } from 'date-fns/locale';
import { environment } from '../environments/environment';
import { routes } from './app.routes';
import { provideKeycloakAuth } from './core/auth';
import { TranslocoHttpLoader } from './core/transloco-http-loader';

/** localStorage key for persisted language (it | en). Restored on app init. */
export const TRANSLOCO_LANG_STORAGE_KEY = 'carplus-lang';

export const AVAILABLE_LANGS = ['it', 'en'] as const;
export const DEFAULT_LANG = 'it';

/** Transloco config (exported for tests: T027 fallback verification). */
export const translocoConfig = {
  defaultLang: DEFAULT_LANG,
  availableLangs: [...AVAILABLE_LANGS],
  reRenderOnLangChange: true,
  fallbackLang: DEFAULT_LANG,
};

/** Exported for tests (T024: verify language restored from localStorage on init). */
export function translocoInitFactory(
  transloco: TranslocoService,
): () => Promise<unknown> {
  return () => {
    const stored =
      typeof localStorage !== 'undefined'
        ? localStorage.getItem(TRANSLOCO_LANG_STORAGE_KEY)
        : null;
    const lang =
      stored &&
      AVAILABLE_LANGS.includes(stored as (typeof AVAILABLE_LANGS)[number])
        ? stored
        : DEFAULT_LANG;
    transloco.setActiveLang(lang);
    return firstValueFrom(transloco.load(lang));
  };
}

export const MY_DATE_FORMATS = {
  parse: {
    dateInput: 'dd/MM/yyyy',
    timeInput: 'HH:mm',
  },
  display: {
    dateInput: 'dd/MM/yyyy',
    monthYearLabel: 'MMM yyyy',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'MMMM yyyy',
    timeInput: 'HH:mm',
    timeOptionLabel: 'HH:mm',
  },
};

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(
      routes,
      withInMemoryScrolling({
        scrollPositionRestoration: 'enabled',
        anchorScrolling: 'enabled',
      }),
    ),
    provideKeycloakAuth({
      keycloak: environment.keycloak,
      apiUrl: environment.apiUrl,
    }),
    provideApi({
      basePath: environment.apiUrl,
    }),
    provideTransloco({
      config: translocoConfig,
      loader: TranslocoHttpLoader,
    }),
    provideTranslocoMessageformat(),
    provideAppInitializer(() =>
      translocoInitFactory(inject(TranslocoService))(),
    ),
    { provide: LOCALE_ID, useValue: 'it-IT' },
    { provide: MAT_DATE_LOCALE, useValue: it },
    provideDateFnsAdapter(MY_DATE_FORMATS),
  ],
};
